import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Card } from '../ui/Card';
import { LoadingSpinner } from '../LoadingSpinner';
import { formatDuration } from '../../utils/format';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  TimeScale
);

interface ListeningTimeData {
  dailyStats: Array<{
    date: string;
    total_duration: number;
    stream_count: number;
    unique_songs: number;
  }>;
  hourlyStats: Array<{
    hour: number;
    total_duration: number;
    stream_count: number;
  }>;
  weeklyStats: Array<{
    day_of_week: number;
    total_duration: number;
    stream_count: number;
  }>;
}

interface ListeningTimeChartProps {
  period: '7d' | '30d' | '90d' | '1y';
}

export function ListeningTimeChart({ period }: ListeningTimeChartProps) {
  const [chartType, setChartType] = useState<'daily' | 'hourly' | 'weekly'>('daily');

  const { data, isLoading, error } = useQuery<ListeningTimeData>({
    queryKey: ['analytics', 'listening-time', period],
    queryFn: async () => {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/analytics/listening-time?period=${period}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch listening time data');
      }
      
      return response.json();
    },
    refetchInterval: 5 * 60 * 1000,
  });

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: '#f0f4f8', // pearl-white
          font: {
            size: 12
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.9)', // dark-void with opacity
        titleColor: '#f0f4f8',
        bodyColor: '#f0f4f8',
        borderColor: '#ec4899', // naia-pink
        borderWidth: 1,
        callbacks: {
          label: function(context: any) {
            if (context.datasetIndex === 0) {
              return `Listening Time: ${formatDuration(context.raw)}`;
            }
            return `${context.dataset.label}: ${context.raw}`;
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#94a3b8', // slate-grey
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.1)',
        }
      },
      y: {
        ticks: {
          color: '#94a3b8',
          callback: function(value: any) {
            if (chartType === 'daily' || chartType === 'weekly') {
              return formatDuration(value);
            }
            return value;
          }
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.1)',
        }
      }
    }
  };

  const getDailyChartData = () => {
    if (!data?.dailyStats) return null;

    const sortedData = [...data.dailyStats].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    return {
      labels: sortedData.map(stat => {
        const date = new Date(stat.date);
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      }),
      datasets: [
        {
          label: 'Listening Time',
          data: sortedData.map(stat => Math.round(stat.total_duration / 60)), // Convert to minutes
          borderColor: '#ec4899', // naia-pink
          backgroundColor: 'rgba(236, 72, 153, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Songs Played',
          data: sortedData.map(stat => stat.stream_count),
          borderColor: '#06b6d4', // cyber-teal
          backgroundColor: 'rgba(6, 182, 212, 0.1)',
          fill: false,
          yAxisID: 'y1',
        }
      ]
    };
  };

  const getHourlyChartData = () => {
    if (!data?.hourlyStats) return null;

    // Fill missing hours with 0 values
    const hourlyData = Array.from({ length: 24 }, (_, hour) => {
      const stat = data.hourlyStats.find(s => s.hour === hour);
      return {
        hour,
        total_duration: stat?.total_duration || 0,
        stream_count: stat?.stream_count || 0
      };
    });

    return {
      labels: hourlyData.map(stat => {
        const hour = stat.hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour} ${ampm}`;
      }),
      datasets: [
        {
          label: 'Listening Time (minutes)',
          data: hourlyData.map(stat => Math.round(stat.total_duration / 60)),
          backgroundColor: 'rgba(236, 72, 153, 0.6)', // naia-pink
          borderColor: '#ec4899',
          borderWidth: 1,
        }
      ]
    };
  };

  const getWeeklyChartData = () => {
    if (!data?.weeklyStats) return null;

    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    // Fill missing days with 0 values
    const weeklyData = Array.from({ length: 7 }, (_, day) => {
      const stat = data.weeklyStats.find(s => s.day_of_week === day);
      return {
        day_of_week: day,
        total_duration: stat?.total_duration || 0,
        stream_count: stat?.stream_count || 0
      };
    });

    return {
      labels: dayNames,
      datasets: [
        {
          label: 'Listening Time',
          data: weeklyData.map(stat => Math.round(stat.total_duration / 60)),
          backgroundColor: [
            'rgba(236, 72, 153, 0.8)', // naia-pink
            'rgba(139, 92, 246, 0.8)', // soft-violet  
            'rgba(6, 182, 212, 0.8)',  // cyber-teal
            'rgba(59, 130, 246, 0.8)', // electric-blue
            'rgba(236, 72, 153, 0.8)', // naia-pink
            'rgba(139, 92, 246, 0.8)', // soft-violet
            'rgba(6, 182, 212, 0.8)',  // cyber-teal
          ],
          borderColor: [
            '#ec4899',
            '#8b5cf6',
            '#06b6d4',
            '#3b82f6',
            '#ec4899',
            '#8b5cf6',
            '#06b6d4',
          ],
          borderWidth: 2,
        }
      ]
    };
  };

  const renderChart = () => {
    switch (chartType) {
      case 'daily':
        const dailyData = getDailyChartData();
        return dailyData ? (
          <Line 
            data={dailyData} 
            options={{
              ...chartOptions,
              scales: {
                ...chartOptions.scales,
                y1: {
                  type: 'linear' as const,
                  display: true,
                  position: 'right' as const,
                  ticks: {
                    color: '#94a3b8',
                  },
                  grid: {
                    drawOnChartArea: false,
                  },
                }
              }
            }} 
          />
        ) : null;

      case 'hourly':
        const hourlyData = getHourlyChartData();
        return hourlyData ? <Bar data={hourlyData} options={chartOptions} /> : null;

      case 'weekly':
        const weeklyData = getWeeklyChartData();
        return weeklyData ? <Doughnut data={weeklyData} options={{
          ...chartOptions,
          scales: undefined, // Remove scales for doughnut chart
        }} /> : null;

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-grey">
        <p>Error loading chart data</p>
      </div>
    );
  }

  if (!data || (!data.dailyStats?.length && !data.hourlyStats?.length && !data.weeklyStats?.length)) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-grey">
        <p>No listening data available for this period</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Chart Type Selector */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setChartType('daily')}
          className={`px-3 py-1 rounded text-sm transition-colors ${
            chartType === 'daily'
              ? 'bg-naia-pink text-white'
              : 'bg-white/10 text-slate-grey hover:bg-white/20'
          }`}
        >
          Daily
        </button>
        <button
          onClick={() => setChartType('hourly')}
          className={`px-3 py-1 rounded text-sm transition-colors ${
            chartType === 'hourly'
              ? 'bg-naia-pink text-white'
              : 'bg-white/10 text-slate-grey hover:bg-white/20'
          }`}
        >
          By Hour
        </button>
        <button
          onClick={() => setChartType('weekly')}
          className={`px-3 py-1 rounded text-sm transition-colors ${
            chartType === 'weekly'
              ? 'bg-naia-pink text-white'
              : 'bg-white/10 text-slate-grey hover:bg-white/20'
          }`}
        >
          By Day of Week
        </button>
      </div>

      {/* Chart Container */}
      <div className="h-64 w-full">
        {renderChart()}
      </div>

      {/* Chart Summary */}
      <div className="mt-4 grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-slate-grey text-xs">Total Time</p>
          <p className="text-pearl-white font-semibold">
            {formatDuration(
              (data.dailyStats?.reduce((sum, stat) => sum + stat.total_duration, 0) || 0)
            )}
          </p>
        </div>
        <div>
          <p className="text-slate-grey text-xs">Total Streams</p>
          <p className="text-pearl-white font-semibold">
            {(data.dailyStats?.reduce((sum, stat) => sum + stat.stream_count, 0) || 0).toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-slate-grey text-xs">Unique Songs</p>
          <p className="text-pearl-white font-semibold">
            {(data.dailyStats?.reduce((sum, stat) => sum + stat.unique_songs, 0) || 0).toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
} 